import { JUNGLE_CLOUDFLARE_BASE_URL } from "../../constants/GlobalTypesAndEnumsAndConstants";
import { getFirebaseAuthorizationHeaders } from "./firebase-auth-headers";
import { getSignedInFirebaseUser } from "./anonymous-read-session";

// Pictures extracted from uploaded PDFs and PowerPoints live in this bucket.
// Since the 2026-11-01 security incident it is private: the stored
// `imageUrl` / `question_side_image_url` is only a stable locator, and the
// browser shows the picture through a short-lived signed URL from the Worker.
export const EXTRACTED_IMAGES_BUCKET_HOST = "wisdolia-pdf-images-input-bucket.s3.us-west-2.amazonaws.com";

// Refresh a signed URL this long before it expires, so an image that starts
// loading just before expiry still loads.
const SIGNED_URL_REFRESH_MARGIN_MILLISECONDS = 60_000;
const DEFAULT_SIGNED_URL_LIFETIME_SECONDS = 900;

type CachedSignedUrl = { signedUrl: string; refreshAtMilliseconds: number };

const signedUrlsByCacheKey = new Map<string, CachedSignedUrl>();
const signedUrlRequestsInFlightByCacheKey = new Map<string, Promise<string>>();

/** The decoded object key of a stored extracted-image URL, or null for any other URL. */
export function getExtractedImageKeyFromStoredUrl(storedUrl: string | null | undefined): string | null {
  if (typeof storedUrl !== "string" || !storedUrl) return null;
  let parsedUrl: URL;
  try {
    parsedUrl = new URL(storedUrl);
  } catch {
    return null;
  }
  if (
    parsedUrl.protocol !== "https:" ||
    parsedUrl.host !== EXTRACTED_IMAGES_BUCKET_HOST ||
    parsedUrl.search ||
    parsedUrl.hash
  ) {
    return null;
  }
  const encodedKey = parsedUrl.pathname.slice(1);
  if (!encodedKey) return null;
  try {
    return decodeURIComponent(encodedKey);
  } catch {
    return null;
  }
}

function getCacheKey(imageKey: string): string {
  // Signed URLs are issued to a signed-in account; never hand one account's
  // cached URL to the next account that signs in on the same tab.
  return `${getSignedInFirebaseUser()?.uid ?? "signed-out"}:${imageKey}`;
}

/** A cached, still-fresh signed URL for this extracted-image key, if any. */
export function getCachedSignedExtractedImageUrl(imageKey: string): string | null {
  const cacheKey = getCacheKey(imageKey);
  const cachedSignedUrl = signedUrlsByCacheKey.get(cacheKey);
  if (!cachedSignedUrl) return null;
  if (Date.now() >= cachedSignedUrl.refreshAtMilliseconds) {
    signedUrlsByCacheKey.delete(cacheKey);
    return null;
  }
  return cachedSignedUrl.signedUrl;
}

/**
 * Asks the Worker for a short-lived signed URL for an extracted-image key.
 * Concurrent requests for the same key share one network call. Rejects when
 * the caller is signed out or the Worker refuses to sign the key.
 */
export function getSignedExtractedImageUrl(imageKey: string): Promise<string> {
  const cachedSignedUrl = getCachedSignedExtractedImageUrl(imageKey);
  if (cachedSignedUrl) return Promise.resolve(cachedSignedUrl);

  const cacheKey = getCacheKey(imageKey);
  const requestInFlight = signedUrlRequestsInFlightByCacheKey.get(cacheKey);
  if (requestInFlight) return requestInFlight;

  const signedUrlRequest = (async () => {
    const firebaseAuthorizationHeaders = await getFirebaseAuthorizationHeaders();
    const response = await fetch(`${JUNGLE_CLOUDFLARE_BASE_URL}/file_or_url/get_presigned_url_for_image_key`, {
      method: "POST",
      headers: { "Content-Type": "application/json", ...firebaseAuthorizationHeaders },
      body: JSON.stringify({ image_key: imageKey }),
    });
    if (!response.ok) {
      throw new Error(`Signing the extracted image failed with HTTP ${response.status}`);
    }
    const responseBody = (await response.json()) as { presigned_url?: unknown; expires_in_seconds?: unknown };
    if (typeof responseBody.presigned_url !== "string" || !responseBody.presigned_url) {
      throw new Error("Signing the extracted image returned no URL");
    }
    const lifetimeSeconds =
      typeof responseBody.expires_in_seconds === "number" && responseBody.expires_in_seconds > 0
        ? responseBody.expires_in_seconds
        : DEFAULT_SIGNED_URL_LIFETIME_SECONDS;
    signedUrlsByCacheKey.set(cacheKey, {
      signedUrl: responseBody.presigned_url,
      refreshAtMilliseconds: Date.now() + lifetimeSeconds * 1000 - SIGNED_URL_REFRESH_MARGIN_MILLISECONDS,
    });
    return responseBody.presigned_url;
  })().finally(() => {
    signedUrlRequestsInFlightByCacheKey.delete(cacheKey);
  });
  signedUrlRequestsInFlightByCacheKey.set(cacheKey, signedUrlRequest);
  return signedUrlRequest;
}

export function resetSignedExtractedImageUrlCacheForTests() {
  signedUrlsByCacheKey.clear();
  signedUrlRequestsInFlightByCacheKey.clear();
}
